<script setup lang="ts" generic="T extends Record<string, any> = Record<string, any>">
import type {
  BubbleListBoundaryState,
  BubbleListEmits,
  BubbleListItemContext,
  BubbleListItemProps,
  BubbleListProps,
  BubbleListScrollState,
  BubbleListFollowReason,
  BubbleListFollowContext,
} from './types'
import { ArrowDownBold } from '@element-plus/icons-vue'
import Bubble from '../Bubble/index.vue'

const scrollContainerRef = ref<HTMLElement | null>(null)
const hasVertical = ref(false)

function checkScrollBar() {
  const el = scrollContainerRef.value
  if (!el) return
  hasVertical.value = el.scrollHeight > el.clientHeight
}

onMounted(() => {
  checkScrollBar()
  const observer = new ResizeObserver(checkScrollBar)
  if (scrollContainerRef.value) observer.observe(scrollContainerRef.value)
  onBeforeUnmount(() => observer.disconnect())
})

const props = withDefaults(defineProps<BubbleListProps<T>>(), {
  list: () => [] as T[],
  autoScroll: true,
  maxHeight: '',
  alwaysShowScrollbar: false,
  backButtonThreshold: 80,
  showBackButton: true,
  backButtonPosition: () => ({ bottom: '20px', left: 'calc(50% - 19px)' }),
  backButtonSmoothScroll: true,
  btnLoading: true,
  btnColor: '#409EFF',
  btnIconSize: 24,
  virtual: false,
  smoothScroll: false,
  topStatus: null,
  bottomStatus: null,
  loadMoreTopThreshold: 100,
  loadMoreBottomThreshold: 100,
})

const emit = defineEmits<BubbleListEmits>()
const MESSAGE_ITEM_TYPES = new Set(['bubble', 'message'])
const BOTTOM_TOLERANCE = 4
const LEGACY_TOLERANCE = 30
const LOAD_RESET_DELAY = 2000

const showBackToBottom = ref(false)
const stopAutoScrollToBottom = ref(false)
const scrollState = ref<BubbleListScrollState>('AT_BOTTOM')
const unreadCount = ref(0)
const lastScrollTop = ref(0)
let legacyResizeObserver: ResizeObserver | null = null
let loadTopResetTimer: number | null = null
let loadBottomResetTimer: number | null = null

const wrapperStyle = computed(() => ({
  '--elx-bubble-list-max-height': props.maxHeight || '100%',
  '--elx-bubble-list-btn-size': `${props.btnIconSize}px`,
  '--elx-bubble-list-btn-color': props.btnColor,
}))

const bottomBoundaryClassName = 'elx-bubble-list__boundary--bottom'

function resolveItemType(item: T | undefined, index: number): string | undefined {
  if (!item) return undefined
  if (typeof props.itemType === 'function') return props.itemType(item, index)
  if (typeof props.itemType === 'string') return item[props.itemType] as string
  return item.itemType ?? item.type
}

function isDefaultBubbleItem(item: T | undefined, index: number) {
  const t = resolveItemType(item, index)?.trim().toLowerCase()
  return !t || MESSAGE_ITEM_TYPES.has(t)
}

function shouldRenderBoundary(status?: BubbleListBoundaryState | null) {
  return !!status && status.type !== 'idle'
}

function resolveBoundaryText(status: BubbleListBoundaryState, position: 'top' | 'bottom') {
  if (status.text) return status.text
  if (status.type === 'loading') return position === 'top' ? '正在加载历史消息...' : '正在加载更多消息...'
  if (status.type === 'no-more') return position === 'top' ? '没有更多历史消息了' : '已经到底了'
  if (status.type === 'error') return '加载失败，请稍后重试'
  return ''
}

function resolveStableItemKey(item: T | undefined, index: number) {
  if (!item) return index
  let resolved: string | number | undefined
  if (typeof props.itemKey === 'function') {
    resolved = props.itemKey(item, index)
  } else if (typeof props.itemKey === 'string') {
    const candidate = item[props.itemKey]
    if (typeof candidate === 'string' || typeof candidate === 'number') resolved = candidate
  }
  if (resolved === undefined) resolved = item.id ?? item.key
  return resolved ?? index
}

function resolveItemKey(item: T | undefined, index: number): string | number {
  return resolveStableItemKey(item, index) ?? index
}

function buildItemContext(item: T, index: number): BubbleListItemContext<T> {
  return {
    item, index,
    itemType: resolveItemType(item, index),
    scrollState: scrollState.value,
    unreadCount: unreadCount.value,
    autoScroll: props.autoScroll,
  }
}

function resolveDistanceMetrics(container: HTMLElement) {
  const rawDistanceToBottom = Math.max(container.scrollHeight - (container.scrollTop + container.clientHeight), 0)
  const bottomBoundaryHeight = container.querySelector<HTMLElement>(`.${bottomBoundaryClassName}`)?.offsetHeight ?? 0
  return {
    rawDistanceToBottom,
    effectiveDistanceToBottom: Math.max(rawDistanceToBottom - bottomBoundaryHeight, 0),
  }
}

function setScrollState(state: BubbleListScrollState) {
  if (scrollState.value !== state) {
    scrollState.value = state
    emit('scrollStateChange', state)
  }
}

function syncScrollStateFromContainer(): BubbleListScrollState {
  const container = scrollContainerRef.value
  if (!container) return scrollState.value
  const { effectiveDistanceToBottom } = resolveDistanceMetrics(container)
  if (effectiveDistanceToBottom <= BOTTOM_TOLERANCE) return 'AT_BOTTOM'
  return unreadCount.value > 0 ? 'HAS_NEW_MESSAGES' : 'SCROLLED_UP'
}

function shouldFollowNewContent(reason: BubbleListFollowReason, item: T, index: number): boolean {
  if (props.shouldFollowContent) {
    const ctx: BubbleListFollowContext<T> = {
      reason, item, index,
      scrollState: syncScrollStateFromContainer(),
      unreadCount: unreadCount.value,
      autoScroll: props.autoScroll,
    }
    return props.shouldFollowContent(ctx)
  }
  if (reason === 'streaming') return props.autoScroll && scrollState.value === 'AT_BOTTOM'
  return props.autoScroll
}

function updateScrollStateFromContainer() {
  const container = scrollContainerRef.value
  if (!container) return
  const { effectiveDistanceToBottom } = resolveDistanceMetrics(container)
  showBackToBottom.value = props.showBackButton && effectiveDistanceToBottom > props.backButtonThreshold
  if (effectiveDistanceToBottom <= BOTTOM_TOLERANCE) {
    setScrollState('AT_BOTTOM')
    if (unreadCount.value !== 0) unreadCount.value = 0
  } else {
    setScrollState(unreadCount.value > 0 ? 'HAS_NEW_MESSAGES' : 'SCROLLED_UP')
  }
}

function handleLegacyScroll() {
  const container = scrollContainerRef.value
  if (!container) return
  const { effectiveDistanceToBottom } = resolveDistanceMetrics(container)
  showBackToBottom.value = props.showBackButton && effectiveDistanceToBottom > props.backButtonThreshold
  stopAutoScrollToBottom.value = effectiveDistanceToBottom > LEGACY_TOLERANCE
  updateScrollStateFromContainer()
}

function handleScroll() {
  const container = scrollContainerRef.value
  if (!container) return
  const { effectiveDistanceToBottom } = resolveDistanceMetrics(container)
  handleLegacyScroll()

  if (container.scrollTop <= props.loadMoreTopThreshold) {
    emit('loadMoreTop')
  }
  if (effectiveDistanceToBottom <= props.loadMoreBottomThreshold) {
    emit('loadMoreBottom')
  }
}

function scrollToBottom(smooth = props.smoothScroll) {
  const container = scrollContainerRef.value
  if (!container) return
  container.scrollTo({ top: container.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
  stopAutoScrollToBottom.value = false
  setScrollState('AT_BOTTOM')
  unreadCount.value = 0
  showBackToBottom.value = false
}

function scrollToTop(smooth = props.smoothScroll) {
  const container = scrollContainerRef.value
  if (!container) return
  container.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' })
}

function scrollToBubble(index: number, smooth = props.smoothScroll) {
  const container = scrollContainerRef.value
  if (!container) return
  const items = container.querySelectorAll<HTMLElement>('.elx-bubble-list__item')
  if (index >= items.length) return
  const target = items[index]
  const containerRect = container.getBoundingClientRect()
  const bubbleRect = target.getBoundingClientRect()
  container.scrollTo({
    top: bubbleRect.top - containerRect.top + container.scrollTop,
    behavior: smooth ? 'smooth' : 'auto',
  })
}

function handleBackButtonClick() {
  scrollToBottom(props.backButtonSmoothScroll)
}

function cleanupLegacyResizeObserver() {
  legacyResizeObserver?.disconnect()
  legacyResizeObserver = null
}

function legacyAutoScroll() {
  const container = scrollContainerRef.value
  if (!container) return
  const listBubbles = container.querySelectorAll<HTMLElement>('.elx-bubble-list__item')
  const lastItem = listBubbles[listBubbles.length - 1]
  if (!lastItem) return

  let shouldScroll = true
  if (listBubbles.length > 1) {
    const secondLast = listBubbles[listBubbles.length - 2]
    const { top, bottom } = secondLast.getBoundingClientRect()
    const { top: cTop, bottom: cBottom } = container.getBoundingClientRect()
    shouldScroll = top < cBottom && bottom > cTop
  }

  if (shouldScroll) {
    scrollToBottom(false)
  }

  cleanupLegacyResizeObserver()
  legacyResizeObserver = new ResizeObserver(() => {
    if (!stopAutoScrollToBottom.value) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'auto' })
    }
  })
  legacyResizeObserver.observe(lastItem)
}

// Watch for list changes
watch(() => props.list.length, (n, o) => {
  if (n > (o ?? 0) && props.autoScroll && scrollState.value === 'AT_BOTTOM') {
    nextTick(() => scrollToBottom(false))
  } else if (n > (o ?? 0) && scrollState.value !== 'AT_BOTTOM') {
    unreadCount.value += n - (o ?? 0)
    setScrollState('HAS_NEW_MESSAGES')
    showBackToBottom.value = props.showBackButton
  }
  // Rebind ResizeObserver on list growth
  if (n > 0) {
    nextTick(() => legacyAutoScroll())
  }
})

// Watch for content streaming on last item
watch(() => {
  const last = props.list[props.list.length - 1]
  return last?.content ?? ''
}, () => {
  if (props.autoScroll && scrollState.value === 'AT_BOTTOM') {
    nextTick(() => scrollToBottom(false))
  }
})

// Watch unreadCount to emit
watch(unreadCount, (c) => {
  emit('unreadCountChange', c)
})

// Boundary status change -> auto scroll
watch(
  () => ({
    bottomType: props.bottomStatus?.type ?? 'idle',
    bottomText: props.bottomStatus?.text ?? '',
  }),
  (cur, prev) => {
    if (!prev || !props.autoScroll || scrollState.value !== 'AT_BOTTOM') return
    if (cur.bottomType !== prev?.bottomType || cur.bottomText !== prev?.bottomText) {
      nextTick(() => scrollToBottom(false))
    }
  }
)

function loadMoreTopComplete() {
  if (loadTopResetTimer !== null) window.clearTimeout(loadTopResetTimer)
  nextTick(() => {
    if (scrollState.value === 'AT_BOTTOM') scrollToBottom(false)
  })
  loadTopResetTimer = window.setTimeout(() => { loadTopResetTimer = null }, LOAD_RESET_DELAY)
}

function loadMoreBottomComplete() {
  if (loadBottomResetTimer !== null) window.clearTimeout(loadBottomResetTimer)
  nextTick(() => {
    if (scrollState.value === 'AT_BOTTOM') scrollToBottom(false)
  })
  loadBottomResetTimer = window.setTimeout(() => { loadBottomResetTimer = null }, LOAD_RESET_DELAY)
}

onMounted(() => {
  if (props.autoScroll && props.list.length > 0) {
    nextTick(() => legacyAutoScroll())
  }
})

onUnmounted(() => {
  cleanupLegacyResizeObserver()
  if (loadTopResetTimer !== null) window.clearTimeout(loadTopResetTimer)
  if (loadBottomResetTimer !== null) window.clearTimeout(loadBottomResetTimer)
})

defineExpose({
  scrollToTop,
  scrollToBottom,
  scrollToBubble,
  get currentScrollState() { return scrollState.value },
  get currentUnreadCount() { return unreadCount.value },
  loadMoreTopComplete,
  loadMoreBottomComplete,
})
</script>

<template>
  <div class="elx-bubble-list" :style="wrapperStyle">
    <div
      ref="scrollContainerRef"
      :class="['elx-bubble-list__list', alwaysShowScrollbar ? 'elx-bubble-list__list--always-scrollbar' : '']"
      @scroll="handleScroll"
    >
      <!-- Top boundary -->
      <div v-if="shouldRenderBoundary(topStatus)" class="elx-bubble-list__boundary elx-bubble-list__boundary--top" :data-state="topStatus?.type">
        <div class="elx-bubble-list__boundary-content">
          <div v-if="topStatus?.type === 'loading'" class="elx-bubble-list__boundary-loading" />
          <span>{{ resolveBoundaryText(topStatus!, 'top') }}</span>
        </div>
      </div>

      <!-- Items -->
      <div
        v-for="(item, index) in list"
        :key="resolveItemKey(item, index)"
        :class="['elx-bubble-list__item', !isDefaultBubbleItem(item, index) ? 'elx-bubble-list__item--custom' : '']"
      >
        <template v-if="isDefaultBubbleItem(item, index)">
          <Bubble
            :content="item.content"
            :placement="item.placement"
            :loading="item.loading"
            :shape="item.shape"
            :variant="item.variant"
            :max-width="item.maxWidth"
            :avatar="item.avatar"
            :avatar-size="item.avatarSize"
            :avatar-gap="item.avatarGap"
            :avatar-shape="item.avatarShape"
            :avatar-src-set="item.avatarSrcSet"
            :avatar-alt="item.avatarAlt"
            :avatar-fit="item.avatarFit"
            :no-style="item.noStyle"
          >
            <template v-if="$slots.avatar" #avatar><slot name="avatar" :item="item" /></template>
            <template v-if="$slots.header" #header><slot name="header" :item="item" /></template>
            <template v-if="$slots.content" #content><slot name="content" :item="item" /></template>
            <template v-if="$slots.footer" #footer><slot name="footer" :item="item" /></template>
            <template v-if="$slots.loading" #loading><slot name="loading" :item="item" /></template>
          </Bubble>
        </template>
        <template v-else>
          <slot name="item" v-bind="buildItemContext(item, index)">
            <div class="elx-bubble-list__embedded-item" :data-item-type="resolveItemType(item, index)">
              {{ item.content || '自定义节点' }}
            </div>
          </slot>
        </template>
      </div>

      <!-- Bottom boundary -->
      <div v-if="shouldRenderBoundary(bottomStatus)" class="elx-bubble-list__boundary elx-bubble-list__boundary--bottom" :data-state="bottomStatus?.type">
        <div class="elx-bubble-list__boundary-content">
          <div v-if="bottomStatus?.type === 'loading'" class="elx-bubble-list__boundary-loading" />
          <span>{{ resolveBoundaryText(bottomStatus!, 'bottom') }}</span>
        </div>
      </div>
    </div>

    <!-- Back to bottom -->
    <Transition name="elx-bubble-list-fade">
      <div
        v-if="showBackToBottom && hasVertical"
        :class="['elx-bubble-list__back-button', $slots.backToBottom ? 'elx-bubble-list__back-button--slot' : '']"
        :style="{ top: backButtonPosition.top, right: backButtonPosition.right, bottom: backButtonPosition.bottom, left: backButtonPosition.left, transform: backButtonPosition.transform }"
      >
        <slot v-if="$slots.backToBottom" name="backToBottom" />
        <button v-else type="button" class="elx-bubble-list__back-to-bottom-default" @click="handleBackButtonClick">
          <el-icon class="elx-bubble-list__back-to-bottom-icon" :style="{ color: btnColor }">
            <ArrowDownBold />
          </el-icon>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@use './style.scss';
</style>
